import React, { useEffect, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import api from "../../lib/api";

export default function StrategyTab() {
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/admin/marketing/strategy").then((r) => setPlan(r.data));
  }, []);

  const generate = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/admin/marketing/strategy/generate");
      setPlan(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Generation failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="strategy-tab">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-slate-600 max-w-xl">A concrete 30-day go-to-market plan, generated from your product, pricing and both target audiences.</p>
        <button onClick={generate} disabled={busy} data-testid="generate-strategy-btn" className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white px-5 py-2.5 rounded-sm font-semibold">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {plan ? "Regenerate plan" : "Generate 30-day plan"}
        </button>
      </div>
      {err && <div className="mt-4 text-sm text-red-600" data-testid="strategy-error">{err}</div>}
      {!plan && !busy && <div className="mt-8 border border-dashed border-slate-300 rounded-sm p-10 text-slate-500" data-testid="strategy-empty">No plan yet. Generate one to get started.</div>}
      {plan && (
        <div className="mt-8 space-y-8" data-testid="strategy-plan">
          <div className="bg-slate-950 text-white p-8 rounded-sm">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-orange-500">Positioning</div>
            <p className="font-display text-xl md:text-2xl font-bold mt-2 leading-snug">{plan.positioning}</p>
            <p className="text-slate-300 mt-4">{plan.summary}</p>
          </div>
          <div>
            <h3 className="font-display text-xl font-bold">Channels</h3>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              {plan.channels?.map((c) => (
                <div key={c.name} className="border border-slate-200 rounded-sm p-5">
                  <div className="font-semibold text-slate-900">{c.name}</div>
                  <p className="text-sm text-slate-600 mt-1">{c.why}</p>
                  <ul className="mt-3 text-sm text-slate-800 list-disc pl-5 space-y-1">{c.weekly_actions?.map((a, i) => <li key={i}>{a}</li>)}</ul>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="font-display text-xl font-bold">Week by week</h3>
            <div className="mt-3 grid grid-cols-1 lg:grid-cols-4 gap-4">
              {plan.weeks?.map((w) => (
                <div key={w.week} className="border border-slate-200 rounded-sm p-5 bg-slate-50">
                  <div className="text-xs uppercase tracking-[0.2em] font-bold text-orange-600">Week {w.week}</div>
                  <div className="font-semibold text-slate-900 mt-1">{w.theme}</div>
                  <ul className="mt-3 text-sm text-slate-800 list-disc pl-5 space-y-1">{w.actions?.map((a, i) => <li key={i}>{a}</li>)}</ul>
                  <div className="mt-3 text-xs font-mono text-slate-500">KPI: {w.kpi}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="font-display text-xl font-bold">KPIs to watch</h3>
            <div className="mt-3 flex flex-wrap gap-2">{plan.kpis?.map((k, i) => <span key={i} className="border border-slate-300 px-3 py-1 text-sm rounded-sm">{k}</span>)}</div>
          </div>
        </div>
      )}
    </div>
  );
}
